import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import ImageUpload from "@/components/tournament/ImageUpload";
import { inputCls as inp } from "@/components/tournament/constants";

const blank = () => ({ id: Math.random().toString(36).slice(2, 10), image: "", title: "", description: "", button_label: "Create Tournament", button_link: "/tournaments/create" });

function SlideEditor({ s, i, n, set, move, remove }) {
  return (
    <div className="card-surface rounded-xl p-4 grid md:grid-cols-[280px_1fr_auto] gap-4" data-testid={`slide-${i}`}>
      <ImageUpload value={s.image} onChange={(image) => set({ image })} testid={`slide-${i}-image`} label="Upload flyer (wide, 1920×900)" />
      <div className="space-y-2">
        <input value={s.title} onChange={(e) => set({ title: e.target.value })} placeholder="Title" maxLength={120} className={inp} data-testid={`slide-${i}-title`} />
        <textarea value={s.description} onChange={(e) => set({ description: e.target.value })} placeholder="Description" maxLength={300} rows={2} className={inp} data-testid={`slide-${i}-description`} />
        <div className="grid grid-cols-2 gap-2">
          <input value={s.button_label} onChange={(e) => set({ button_label: e.target.value })} placeholder="Button label" maxLength={40} className={inp} data-testid={`slide-${i}-btn-label`} />
          <input value={s.button_link} onChange={(e) => set({ button_link: e.target.value })} placeholder="/tournaments/create or https://…" maxLength={300} className={inp} data-testid={`slide-${i}-btn-link`} />
        </div>
      </div>
      <div className="flex md:flex-col gap-1">
        <button onClick={() => move(-1)} disabled={i === 0} className="p-2 rounded-md text-white/50 hover:text-white disabled:opacity-20"><ArrowUp size={15} /></button>
        <button onClick={() => move(1)} disabled={i === n - 1} className="p-2 rounded-md text-white/50 hover:text-white disabled:opacity-20"><ArrowDown size={15} /></button>
        <button onClick={remove} className="p-2 rounded-md text-white/50 hover:text-red-400" data-testid={`slide-${i}-remove`}><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

export default function AdminHomepage() {
  const { settings, refresh } = useSettings();
  const [slides, setSlides] = useState([]);
  const [social, setSocial] = useState({ instagram: "", facebook: "", threads: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (settings) { setSlides(settings.hero_slides || []); setSocial({ instagram: "", facebook: "", threads: "", ...(settings.social || {}) }); } }, [settings]);

  const setAt = (i, p) => setSlides(slides.map((s, idx) => (idx === i ? { ...s, ...p } : s)));
  const move = (i, d) => { const a = [...slides]; [a[i], a[i + d]] = [a[i + d], a[i]]; setSlides(a); };
  const save = async () => {
    const bad = Object.values(social).find((v) => v && !/^https:\/\//.test(v));
    if (bad) { toast.error("Social links must start with https://"); return; }
    setBusy(true);
    try { await api.put("/admin/settings", { hero_slides: slides, social }); await refresh(); toast.success("Homepage saved"); }
    catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-homepage">
      <div className="flex items-center justify-between">
        <div><h2 className="text-xl font-bold text-white">Homepage Hero & Social Media</h2><p className="text-sm text-white/50">Hero image(s) with title, description and button text/link — plus social media links in the footer.</p></div>
        <button onClick={save} disabled={busy} data-testid="homepage-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-white font-bold disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Homepage</button>
      </div>
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-white">Hero slides ({slides.length})</h2>
          <button onClick={() => setSlides([...slides, blank()])} data-testid="add-slide-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white text-sm font-semibold hover:bg-white/20"><Plus size={14} /> Add slide</button>
        </div>
        <div className="space-y-3">
          {slides.map((s, i) => <SlideEditor key={s.id || i} s={s} i={i} n={slides.length} set={(p) => setAt(i, p)} move={(d) => move(i, d)} remove={() => setSlides(slides.filter((_, idx) => idx !== i))} />)}
          {!slides.length && <div className="card-surface rounded-xl p-8 text-center text-white/40">No slides yet. A default banner is shown until you add one.</div>}
        </div>
      </section>
      <section className="card-surface rounded-xl p-5 space-y-3 max-w-2xl">
        <h2 className="font-bold text-white">Social media</h2>
        {["instagram", "facebook", "threads"].map((k) => (
          <div key={k}><label className="block text-xs font-semibold text-white/50 mb-1 capitalize">{k}</label>
            <input value={social[k]} onChange={(e) => setSocial({ ...social, [k]: e.target.value })} placeholder={`https://www.${k === "threads" ? "threads.net" : `${k}.com`}/7legendary`} className={inp} data-testid={`social-input-${k}`} /></div>
        ))}
      </section>
    </div>
  );
}
